import useRouter from "common/hooks/use-router";
import useAuth from "hooks/useAuth";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Zone } from "types";
import type { TableColumn } from "common/components/DataTable/DataTable";
import Badges from "components/badges/Badges";
import CrudList from "common/components/Crud/CrudList";
import type { CrudHeaderAction } from "common/components/Crud/components/CrudHeader";
import type { CrudFilter } from "common/components/Crud/components/CrudFilters";
import CrudEdit from "common/components/Crud/CrudEdit";
import ZoneForm from "./ZoneForm";
import fetchJSON from "common/utils/fetchJSON";
import AlertsContext from "providers/AlertProvider";

function SidebarZoneForm({ ...props }: any) {
  return <CrudEdit collectionName="zones" Form={ZoneForm} {...props} />;
}

function Zones() {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const { user: currentUser } = useAuth();
  const { setAlert } = useContext(AlertsContext);
  const [syncing, setSyncing] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const canManage = currentUser?.isAdmin || currentUser?.isSuperadmin;

  useEffect(() => {
    fetchJSON({ url: "zones/refresh-dates", method: "GET" })
      .then(() => setRefreshTrigger((n) => n + 1))
      .catch(() => {});
  }, []);

  const handleDetail = useCallback(
    (id: any) => {
      navigate(`/zones/${id}`);
    },
    [navigate]
  );

  const handleToggleActive = useCallback(async (zone: Zone, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await fetchJSON({ url: `zones/${zone.id}`, method: "PUT", payload: { data: { enabled: zone.enabled === false ? true : false } } });
      setRefreshTrigger((n) => n + 1);
    } catch (e: any) {
      setAlert(e?.message ?? "Erreur", "error");
    }
  }, [setAlert]);

  const handleSync = useCallback(async () => {
    setSyncing(true);
    try {
      const result = await fetchJSON({ url: "zones/sync", method: "POST" });
      setAlert(
        `Synchronisation terminée : ${result.created} zone(s) ajoutée(s), ${result.skipped} ignorée(s).`,
        "success"
      );
      setRefreshTrigger((n) => n + 1);
    } catch (e: any) {
      setAlert(e?.message ?? "Erreur lors de la synchronisation", "error");
    } finally {
      setSyncing(false);
    }
  }, [setAlert]);

  function formatDate(value: string) {
    return new Date(value).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  function dateBadge(value: string | null | undefined) {
    if (!value) {
      return <Badges items={[{ name: "Données absentes" }]} iconName="ExclamationCircleIcon" className="text-red-600 [&_span]:border-red-300 [&_span]:text-red-600" />;
    }
    return <Badges items={[{ name: formatDate(value) }]} iconName="CalendarDaysIcon" className="text-green-600 [&_span]:border-green-400 [&_span]:text-green-700" />;
  }

  const columns: TableColumn<Zone>[] = useMemo(
    () => [
      {
        field: "enabled",
        header: "Statut",
        customRender: (rowData: Zone) => canManage ? (
          <button
            onClick={(e) => handleToggleActive(rowData, e)}
            className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${rowData.enabled !== false ? "bg-green-500" : "bg-gray-300"}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${rowData.enabled !== false ? "translate-x-4" : "translate-x-0"}`} />
          </button>
        ) : (
          <div className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent ${rowData.enabled !== false ? "bg-green-500" : "bg-gray-300"}`}>
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow ${rowData.enabled !== false ? "translate-x-4" : "translate-x-0"}`} />
          </div>
        ),
      },
      {
        field: "name",
        header: t("zones.name"),
        sortable: true,
        renderType: "bold",
      },
      {
        field: "firstRainHistoryDate",
        header: "1ère pluie historique",
        customRender: (rowData: Zone) => dateBadge(rowData.firstRainHistoryDate),
      },
      {
        field: "lastRainHistoryDate",
        header: "Dernière pluie historique",
        customRender: (rowData: Zone) => dateBadge(rowData.lastRainHistoryDate),
      },
      {
        field: "lastForecastDate",
        header: "Dernière prévision",
        customRender: (rowData: Zone) => dateBadge(rowData.lastForecastDate),
      },
    ],
    [t, canManage, handleToggleActive]
  );

  const actions: CrudHeaderAction[] = useMemo(() => {
    if (!canManage) return [];
    return [
      {
        title: syncing ? "Synchronisation…" : "Synchroniser les rizières",
        icon: "ArrowPathIcon",
        action: () => { handleSync(); },
        disabled: syncing,
        type: "white",
        className: "flex flex-row-reverse",
      },
      {
        title: t("common.new"),
        icon: "PlusCircleIconSolid",
        action: "openSidebar",
        type: "white",
        className: "flex flex-row-reverse",
      },
    ];
  }, [t, syncing, handleSync, canManage]);

  const filters: CrudFilter[] = useMemo(() => {
    return [
      {
        name: "name$containsi",
        label: t("locations.name"),
        renderType: "input",
      },
    ];
  }, [t]);

  return (
    <CrudList
      collectionName="zones"
      columns={columns}
      headerActions={actions}
      filters={filters}
      defaultSort="name:ASC"
      canDelete={canManage}
      canEdit={canManage}
      icon="MapPinIcon"
      onNavigate={handleDetail}
      formMode="sidebar"
      SidebarFormComponent={SidebarZoneForm}
      actionsHeader=""
      refreshTrigger={refreshTrigger}
    />
  );
}

export default Zones;
